import React, { useCallback, useEffect, useState } from 'react';
import { withErrorBoundary, Loader, getTranslatedText } from '@nile/components';
import {
  ActionList,
  Button,
  DisplayText,
  Popover,
  TextField,
  TextStyle,
  Tippy
} from '@superops/ui-design-system';
import { GET_CHARTS } from '@nile/reports/src/modules/charts/graphql';
import { AccountClient, useCachedQuery } from '@nile/graphql';
import { FormattedMessage, useIntl } from 'react-intl';
import { CHART_ICON } from '@nile/reports/src/modules/charts/utils/chart-transformation';
import { GET_REPORT_MODULES } from '@nile/reports/src/modules/reports/graphql';
import ChartHome from '@nile/reports/src/modules/charts/components/pages/ChartHome';
import { SVGIcon } from '@superops/super-icons';
import { debounce } from 'lodash';
import { ChartItemWrapper, Elements, DefaultViewWrapper } from './styles';

function ChartLibrary(props) {
  const intl = useIntl();
  const { show, selectedCharts, onDrag } = props;

  const [charts, setCharts] = useState([]);
  const [chartClone, setChartClone] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [chartLibList, setChartLibList] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('allCharts');
  const [listLoading, setListLoading] = useState(true);
  const [openChart, setOpenChart] = useState(false);

  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(
    () => setPopoverActive((active) => !active),
    []
  );

  // List of entities available

  const { loading: reportModulesLoading, data: reportModules } = useCachedQuery(
    GET_REPORT_MODULES,
    {
      client: AccountClient
    }
  );

  useEffect(() => {
    if (reportModules?.getReportModules) {
      let allModules = reportModules?.getReportModules.map(
        ({ name, label }) => ({
          value: name,
          label: getTranslatedText(intl, label)
        })
      );
      allModules = [
        {
          value: 'allCharts',
          label: getTranslatedText(intl, 'reports.common.allWidgets')
        },
        {
          value: 'defaultCharts',
          label: getTranslatedText(intl, 'reports.common.defaultWidgets')
        },
        ...allModules
      ];
      setModules(allModules);
    }
  }, [reportModules]);

  // Entity dropdown change

  const handleModuleChange = (val) => {
    setSelectedModule(val?.value);
    togglePopoverActive(false);
  };

  const filterChartList = async () => {
    if (selectedModule === 'allCharts') {
      if (searchValue === '') {
        await setChartLibList(charts);
        setListLoading(false);
        return;
      }
      const filteredChart = charts.filter((el) =>
        el?.name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
      );
      await setChartLibList(filteredChart);
      setListLoading(false);
      return;
    }
    if (selectedModule === 'defaultCharts') {
      const entityRelatedChart = charts.filter(
        (el) =>
          el.systemChart &&
          el?.name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
      );
      await setChartLibList(entityRelatedChart);
      setListLoading(false);
      return;
    }
    const entityRelatedChart = charts.filter(
      (el) =>
        el.entity === selectedModule &&
        el?.name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );
    await setChartLibList(entityRelatedChart);
    setListLoading(false);
  };

  useEffect(() => {
    filterChartList();
  }, [selectedModule, searchValue]);

  const onSearch = (val) => {
    setSearchValue(val);
  };
  const filterTable = useCallback(debounce(onSearch, 1000), []);

  // To get all charts

  const {
    loading: chartsLoading,
    data,
    refetch: refetchChart
  } = useCachedQuery(GET_CHARTS, {
    variables: {
      listInfo: {
        sort: [
          {
            attribute: 'createdTime',
            order: 'DESC'
          }
        ]
      }
    },
    client: AccountClient
  });

  useEffect(() => {
    if (data) {
      const { getCharts } = data;
      if (getCharts) {
        setCharts(getCharts.charts);
        setChartClone(getCharts.charts);
      }
    }
  }, [data]);

  useEffect(() => {
    filterChartList();
  }, [charts]);

  // Remove existing from library

  const filterChartFromLibrary = () => {
    return selectedCharts
      ? chartClone.filter(
          (chrt) =>
            !selectedCharts.filter((crt) => crt.chartId === chrt.chartId).length
        )
      : chartClone;
  };

  useEffect(() => {
    if (chartClone.length) {
      const filteredCharts = filterChartFromLibrary();
      setCharts(filteredCharts);
    }
  }, [chartClone, selectedCharts]);

  const activator = (
    <Button
      id="statusChange"
      plain
      monochrome
      onClick={togglePopoverActive}
      loading={reportModulesLoading}
    >
      <div className="flex center">
        <DisplayText element="h2" weight="SemiBold">
          {modules.find((mod) => mod.value === selectedModule)?.label}
        </DisplayText>
        <div className="fx jctr ml-10">
          <SVGIcon name="ArrowDown" color="black !important" size="lg" />
        </div>
      </div>
    </Button>
  );

  return (
    <Elements
      className="position-absolute scroll bg-white pd-x20 pb20 card-border rounded-4px"
      navOpen={show}
    >
      <div className="sticky-section">
        <div className="fx pb10">
          <div className="f11">
            <div className="w250" superid="moduleSelector">
              <Popover
                active={popoverActive}
                activator={activator}
                preferredAlignment="left"
                onClose={togglePopoverActive}
              >
                <ActionList
                  actionRole="menuItem"
                  items={modules.map((el) => ({
                    content: <TextStyle>{el.label}</TextStyle>,
                    onAction: () => handleModuleChange(el)
                  }))}
                />
              </Popover>
            </div>
          </div>
          <div className="jend ptr">
            <Button
              onClick={() => setOpenChart(true)}
              icon={<SVGIcon name="Add" />}
            />
          </div>
        </div>
        <div className="pb10">
          <TextField
            autoComplete="off"
            value={searchTerm}
            onChange={(val) => {
              filterTable(val);
              setSearchTerm(val);
            }}
            prefix={<SVGIcon name="Search" />}
            suffix={
              searchTerm ? (
                <div
                  className="z21 ptr position-relative"
                  aria-hidden
                  onClick={() => {
                    filterTable('');
                    setSearchTerm('');
                  }}
                >
                  <SVGIcon name="CircleCancelMinor" />
                </div>
              ) : (
                <></>
              )
            }
            placeholder="Search"
            variant="outline"
          />
        </div>
      </div>
      {(chartsLoading || listLoading) && <Loader />}
      <div className="flex wrap pt10 g20">
        {!chartsLoading &&
          (chartLibList.length > 0 ? (
            chartLibList.map((item, index) => (
              <ChartItem
                data={item}
                onDrag={onDrag}
                index={index}
                intl={intl}
              />
            ))
          ) : (
            <>
              {(chartClone.length === 0 || chartLibList.length === 0) &&
                !listLoading && (
                  <div className="pb-10">
                    <TextStyle>
                      <FormattedMessage id="reports.widgetLibrary.search.noResult" />
                    </TextStyle>
                  </div>
                )}
            </>
          ))}
      </div>
      {openChart && (
        <ChartHome
          callBack={() => {
            setOpenChart(false);
            refetchChart();
          }}
          mode="create"
        />
      )}
    </Elements>
  );
}

const ChartItem = (props) => {
  const { data, onDrag, intl } = props;
  return (
    <ChartItemWrapper
      className="flex-center fcol card-border position-relative p15 droppable-element "
      draggable
      unselectable="on"
      onDragStart={(e) => {
        onDrag(data);
        e.dataTransfer.setData('text/plain', '');
      }}
    >
      {data?.systemChart && (
        <DefaultViewWrapper>
          {getTranslatedText(intl, 'common.label.default')}
        </DefaultViewWrapper>
      )}
      <div className="flex actr jctr">
        <SVGIcon
          name={
            data?.defaultChartType
              ? CHART_ICON[data?.defaultChartType]
              : 'NumberChart'
          }
          size="xxl"
        />
      </div>
      <div className="flex jctr fw p4">
        <div className="ellipsis">
          <Tippy
            html={
              <div className="bg-white p10 rounded-4px card-border maxW350">
                <div className="mb4">
                  <TextStyle weight="SemiBold">{data.name}</TextStyle>
                </div>
                <TextStyle>{data.description}</TextStyle>
              </div>
            }
            trigger="mouseenter"
            allowHTML
            theme="light"
            distance={5}
          >
            <TextStyle>{data.name}</TextStyle>
          </Tippy>
        </div>
      </div>
    </ChartItemWrapper>
  );
};

export default withErrorBoundary(ChartLibrary);
